/**
 * **翻牌** —— 牌背揭成牌面那一下。
 *
 * 评审给的动作写得很具体：
 *
 * > 先生成一个隐藏的实际牌面垫在牌背底下，然后二者一起左右 pitch 180 度，
 * > 刚好转到 90 度时牌背隐藏牌面显示，转完删去牌背，然后牌面进入手牌。
 *
 * 所以节点结构**必须**是"牌面在下、牌背在上"：
 *
 * ```
 *   wrapper          ← 整体做左右翻转（Pixi 2D 里就是 scale.x）
 *     ├ face    (牌面)  ← 一开始被牌背挡住
 *     └ back    (牌背)  ← 转 90° 之后隐藏，牌面就露出来了
 * ```
 *
 * ⚠️ **不是"两张牌各转一半"**，是同一个 wrapper 在转 —— 这才是"二者一起"。
 *
 * ## 为什么是 `scale.x` 而不是真的绕 Y 轴转
 *
 * Pixi 的 2D 变换没有 `rotationY`（那要三维）。而"绕竖直轴转 180°"在正交投影下
 * 恰好等于 `scale.x: 1 → -1`。转到 -1 会让牌面**镜像**（文字反着写），
 * 所以实际做法是 `1 → 0` 再 `0 → 1`：过了 90° 之后用**没有镜像的另一半**
 * 接着转完。视觉上和转满 180° 一模一样，而文字始终是正的。
 *
 * ## ⚠️ 超采样：牌面是**按更大的尺寸画**的
 *
 * 牌面走的是矢量 + `Text`（见 `painter.ts`），而 `Text` 的字形纹理是烘一次的。
 * 这张牌在濒死时要撑到 2 倍，字形纹理跟着放大两倍采样就会糊 ——
 * 所以牌面按 `height × supersample` 画、整体缩回 `1 / supersample`
 * （见 `CardFlipOptions.supersample`）。**版面尺寸不变**，变的只是"画得多细"。
 *
 * ## 时长
 *
 * 两半各 `DURATION.fast`（110ms），一共 220ms —— 正好落在
 * `visual-design.md` §6 那条「90–220ms」的上沿。曲线用 `easeOut`（急停），
 * **不过冲**（§6 记着弹簧那版被否了）。
 */

import { Container } from 'pixi.js';
import type { Renderer, Ticker } from 'pixi.js';
import { CardArtRegistry, specArt } from '../card/art';
import { backFaceTexture } from '../card/backFaceTexture';
import { CardView } from '../card/cardView';
import { CardFaceCache, faceSize } from '../card';
import type { CardDef } from '../../core/card';
import { createAnimated, DURATION, drive, undrive } from '../ui/motion';

export interface CardFlipOptions {
  readonly renderer: Renderer;
  readonly def: CardDef;
  /** 牌高（逻辑像素） */
  readonly height: number;
  /** 复用手牌那份缓存。不给就自己烘一张（会慢一点） */
  readonly faces?: CardFaceCache;
  /**
   * **超采样倍率。** 牌面按 `height × supersample` 画，再整体缩回
   * `1 / supersample` —— 于是"画多大"和"显示多大"**解耦**了。
   *
   * ## 为什么必须有它
   *
   * 评审：
   *
   * > 牌面上的文字也存在尺寸放大后变糊的问题。
   *
   * 根因在这一层：牌面是**矢量 + Pixi `Text`** 画的（见 `painter.ts`），
   * 而 `Text` 的字形是**一次性烘成画布纹理**的，烘的时候按它自己的字号
   * （`fontSize × 渲染器分辨率`）。翻牌这张牌在濒死时要撑到
   * `DYING_GROW_TO`（= 2）倍，于是那张字形纹理被**放大两倍采样** ——
   * 笔画边缘直接糊掉，而牌是小字号的，最糊的就是字。
   *
   * 按 2 倍画再缩回去之后，同一片笔画在屏幕上仍然是原来那么大，
   * 但每个屏幕像素都落在**独立的纹素**上（DPR 2 的屏上甚至还有余量）。
   *
   * ⚠️ 不能用"给小字号塞一个更大的 fontSize 再算缩放"来实现 —— 那会把
   *    版面（行距、字距、`fitWidth` 的实测宽度）全部按另一个尺度算一遍。
   *    这里改的是**几何被画多大**，版面参数一个字都没动。
   *
   * ⚠️ 翻牌那一半（`#turn`）的 `scale.x` 要**一起**除掉这个倍率，
   *    见 `#applyTurn` —— 漏了的话牌会大一圈，而且每帧都会被改写回 1。
   */
  readonly supersample?: number;
  /**
   * **这张牌一开始就是正面朝上的**（出牌动画那种）。
   *
   * 抽牌动画里牌背翻成牌面是"揭晓"；而出牌的牌在手里就已经看得见了，
   * 再翻一次反而是错的。给 `true` 时：跳过整段翻面，直接把状态按到
   * "牌面朝外"（`#turn = 1`、牌背隐藏、`finished = true`）。
   *
   * ⚠️ 只影响这一张自己的初始姿态，`flip()` 与抽牌动画一个字都没改。
   */
  readonly faceUp?: boolean;
}

export class CardFlip {
  readonly view: Container;

  readonly #faces: CardFaceCache;
  readonly #ownFaces: boolean;
  readonly #back: CardView;
  readonly #face: CardView;
  /** 1 = 牌背朝外，0 = 转到 90°（侧对），1 = 牌面朝外。中间那一下换面 */
  readonly #turn = createAnimated(1);

  /** 超采样倍率（见 `CardFlipOptions.supersample`） */
  readonly #supersample: number;

  #showingBack = true;
  #done = false;
  #pushed = false;
  /**
   * 登记在 `ui/motion.ts` 步进器上的那个动画。
   *
   * ⚠️ 必须留着它：销毁时要在 `undrive()` 里摘掉（见那个函数的说明）——
   * 不摘的话，这一屏已经拆了、翻牌还挂在每帧队列上，
   * 下一帧就会往已销毁的容器上写 `scale.x`，整个 ticker 就此停摆。
   */
  #driver: { advance(deltaMS: number): boolean } | null = null;

  constructor(options: CardFlipOptions) {
    const supersample = Math.max(1, options.supersample ?? 1);
    this.#supersample = supersample;
    /** 版面尺寸（调用方看到的"这张牌多大"） */
    const size = faceSize(options.height);
    this.#size = { width: size.width, height: size.height };
    /**
     * ⚠️ **画的是这个尺寸**（比版面大 `supersample` 倍），显示时再缩回去。
     *    下面所有几何（字、出血、剪裁）一律用它。
     */
    const paint = faceSize(options.height * supersample);

    this.view = new Container();
    this.view.label = 'card-flip';

    // ⚠️ 牌面**先加**（在下层）。评审那句话说得很准：
    //    「先生成一个隐藏的实际牌面垫在牌背底下」
    //
    // ★ 节点统一成 `CardView`：牌面走共享的 `CardFaceCache`（含出血的纹理），
    //   牌背走共享的牌背纹理。贴图、假透视、四角都由 `CardView` 负责，
    //   它按"牌 + 出血"铺网格 —— 于是出框部分和手牌**同一套裁切**，
    //   不需要再自己加遮罩。这里只负责翻转。
    const paintSize = { width: paint.width, height: paint.height };
    this.#faces = options.faces ?? new CardFaceCache(options.renderer, new CardArtRegistry(specArt));
    this.#ownFaces = options.faces === undefined;
    this.#face = new CardView({ texture: this.#faces.get(options.def, paintSize), size: paintSize });
    this.#back = new CardView({ texture: backFaceTexture(options.renderer, paintSize), size: paintSize });

    // `CardView` 的 `position` 就是**牌心** —— 摆到 (w/2, h/2) 后牌正好占
    // (0,0)..(w,h)，和旧节点视觉一致；翻转交给外层 `view.scale.x`
    for (const node of [this.#face, this.#back]) node.position.set(paint.width / 2, paint.height / 2);

    this.view.addChild(this.#face, this.#back);

    this.view.eventMode = 'none';

    /**
     * ★ **出牌动画的牌一开始就是正面朝上的**（见 `faceUp`）。
     *
     * 跳过翻面：牌背藏掉、翻牌值按到 1、自己标记完成 —— 于是 `flip()`
     * 变成空操作，而 `#applyTurn()` 摆出来的是"正面、正常大小"。
     */
    if (options.faceUp === true) {
      this.#showingBack = false;
      this.#back.visible = false;
      this.#turn.snap(1);
      this.#done = true;
      this.#applyTurn();
    }
  }

  /**
   * 牌本身有多大（调用方要摆位置）。
   *
   * ⚠️ 用构造时算好的那份，**不要**读 `view.width` —— 那个数会被翻转的
   * `scale.x` 影响（转到 90° 那一帧它接近于零），而调用方要的是"这张牌
   * 有多大"，不是"现在看起来多宽"。
   */
  get size(): { readonly width: number; readonly height: number } {
    return this.#size;
  }

  readonly #size: { readonly width: number; readonly height: number };

  get finished(): boolean {
    return this.#done;
  }

  /**
   * 开始翻。幂等。
   *
   * `totalMs` 是**整段**翻牌的时长，两半各占一半 —— 所以每一半会落在
   * `visual-design.md` §6 那条 90–220ms 的区间里，前提是 `totalMs` 在
   * 180–440 之间。抽牌动画把**飞行的时长**传进来，见 `drawReveal.ts`。
   */
  flip(totalMs: number = DURATION.fast * 2): void {
    if (this.#done || this.#pushed) return;
    this.#pushed = true;
    const half = Math.max(1, totalMs / 2);
    this.#turn.to(0, half);
    this.#driver = {
      advance: (deltaMS) => {
        const moving = this.#turn.advance(deltaMS);
        this.#applyTurn();
        if (!moving && !this.#done) {
          // 过了 90°：牌背隐藏、牌面显示，然后**接着转完另一半**
          if (this.#showingBack) {
            this.#showingBack = false;
            this.#back.visible = false;
            this.#turn.snap(0);
            this.#turn.to(1, half);
            this.#applyTurn();
            return true;
          }
          this.#done = true;
        }
        return !this.#done;
      },
    };
    drive(this.#driver);
  }

  /** 也可以手动每帧推（不用 `flip()` 的登记表时） */
  update(ticker: Ticker): void {
    void ticker;
    this.#applyTurn();
  }

  #applyTurn(): void {
    // 已经拆掉了就别写了（Pixi 销毁容器时会把 `_scale` 置空，
    // 那一写就是 TypeError，而且是从 Ticker 里抛出去的）
    if (this.view.destroyed) return;
    const t = this.#turn.value;
    const s = this.#supersample;
    // 侧对的时候 `scale.x` 是 0 —— Pixi 会把整棵子树当退化矩阵跳过绘制，
    // 所以给一个极小的下限，免得"转到 90° 那一帧"整张牌消失成一片空白
    //
    // ⚠️ 两个轴都要**除掉超采样倍率**：画的是 `height × s`，缩回去才是版面尺寸
    this.view.scale.set(Math.max(0.02, t) / s, 1 / s);
    this.#back.visible = this.#showingBack;
  }

  destroy(): void {
    if (this.#driver !== null) {
      undrive(this.#driver);
      this.#driver = null;
    }
    this.view.destroy({ children: true });
    if (this.#ownFaces) this.#faces.destroy();
  }
}
